import { useMemo } from 'react';
import { SourcesStatsResponse } from '../../../shared/models/monitor/data/sources/SourcesStatsResponse';
import { SourceStats } from '../../../shared/types/monitor/data/sources/SourceStats';
import { calculatePercentageShare } from '../../../shared/helpers/StatsHelpers';

export type SourceStatsWithShare = SourceStats & {
  share: number;
  // shareChange is NOT here - it's only in comparison hooks
};

export type SourcesStatsWithShare = {
  sources: SourceStatsWithShare[];
  pagination?: SourcesStatsResponse['pagination'];
  totalCount?: number;
};

/**
 * Hook to calculate share percentages for sources
 * Uses totalCount (sum of all mentions across entire dataset) for accurate share percentages
 */
export const useSourcesStatsShare = (
  stats: SourcesStatsResponse | undefined
): SourcesStatsWithShare | undefined => {
  return useMemo(() => {
    if (!stats?.sources) {
      return stats
        ? { sources: [], pagination: stats.pagination, totalCount: stats.totalCount }
        : undefined;
    }

    // Calculate total for share calculations
    // Use totalCount (sum of all mentions across entire dataset) for accurate share percentages
    // totalCount is ONLY for share calculations, not for pagination
    const calculateTotalFromSources = (sources: typeof stats.sources) => {
      return sources.reduce((sum, source) => sum + source.count, 0);
    };

    // Use totalCount if available, otherwise fallback to sum of displayed sources
    // Do NOT use pagination?.totalItems for share calculations (it's only for pagination)
    const total = stats.totalCount ?? calculateTotalFromSources(stats.sources);
    const safeTotal = total > 0 ? total : 1;

    // Calculate share for each source
    const sourcesWithShare: SourceStatsWithShare[] = stats.sources.map((source) => ({
      ...source,
      share: parseFloat(calculatePercentageShare(source.count, safeTotal, false)),
    }));

    return {
      sources: sourcesWithShare,
      pagination: stats.pagination,
      totalCount: stats.totalCount,
    };
  }, [stats]);
};
